import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../ui/utils";

const isFlatpickrInteraction = (target: EventTarget | null, originalEvent?: PointerEvent): boolean => {
  const pathTargets = typeof originalEvent?.composedPath === "function"
    ? originalEvent.composedPath()
    : [];

  return [target, ...pathTargets].some((node) => (
    node instanceof HTMLElement && Boolean(node.closest(".flatpickr-calendar"))
  ));
};

export interface ModalProps {
  isOpen: boolean;
  toggle?: () => void;
  centered?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  variant?: "center" | "full" | "sheet";
  title?: string;
  children: React.ReactNode;
  className?: string;
  overlayClassName?: string;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  toggle,
  centered = false,
  size = 'md',
  variant,
  title = "대화상자",
  children,
  className = '',
  overlayClassName = ''
}) => {
  const resolvedVariant = variant ?? (size === "full" ? "full" : "center");
  const sizeClasses = {
    sm: "max-w-sm",
    md: "max-w-[480px]",
    lg: "max-w-lg",
    xl: "max-w-xl",
    full: "max-w-[640px]",
  };

  return (
    <DialogPrimitive.Root
      open={isOpen}
      onOpenChange={(open) => (!open ? toggle?.() : undefined)}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[10000] bg-black/40 backdrop-blur-sm",
            overlayClassName,
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 z-[10001] grid w-full gap-4 border shadow-lg duration-200",
            resolvedVariant === "center" &&
              "fixed top-1/2 left-1/2 max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-xl p-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            resolvedVariant === "sheet" &&
              "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            resolvedVariant === "full" &&
              "fixed inset-y-0 inset-x-0 mx-auto h-full w-full max-w-[640px] rounded-none border-0 p-0",
            resolvedVariant === "sheet" &&
              "fixed inset-x-0 bottom-0 left-1/2 w-full max-w-[640px] -translate-x-1/2 rounded-t-3xl border-0 p-0",
            resolvedVariant === "center" && sizeClasses[size],
            !centered && resolvedVariant === "center" && "top-10 -translate-y-0",
            resolvedVariant === "full" && "bg-white",
            resolvedVariant === "sheet" && "bg-white",
            className,
          )}
          onPointerDownOutside={(event) => {
            if (isFlatpickrInteraction(event.target, event.detail.originalEvent)) {
              event.preventDefault();
            }
          }}
        >
          <DialogPrimitive.Title className="sr-only">
            {title}
          </DialogPrimitive.Title>
          {resolvedVariant === "center" ? (
            <div className="p-5 max-h-[90vh] overflow-y-auto">
              {children}
            </div>
          ) : (
            <div
              className={cn(
                "w-full overflow-y-auto",
                resolvedVariant === "full" && "h-full",
                resolvedVariant === "sheet" && "max-h-[85vh]",
              )}
            >
              {children}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
